import React, { useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useLang } from "../context/LanguageContext";
import CyberBackground from "../components/CyberBackground";
import ArticleBlocks from "../components/resource/ArticleBlocks";
import ConsultForm from "../components/ConsultForm";
import CTABand from "../components/resource/CTABand";
import { RESOURCES } from "../i18n/resources";
import { ArrowLeft, Building2, ArrowRight } from "lucide-react";

export default function CaseStudyDetail() {
  const { slug } = useParams();
  const { t, dir, lang } = useLang();
  const navigate = useNavigate();
  const flip = dir === "rtl" ? "scaleX(-1)" : "none";
  const items = RESOURCES[lang].caseStudies;
  const cs = items.find((c) => c.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!cs) navigate("/case-studies");
  }, [cs, navigate, slug]);

  if (!cs) return null;
  const others = items.filter((c) => c.slug !== slug);

  return (
    <div>
      <section className="relative pt-36 pb-14 overflow-hidden">
        <div className="absolute inset-0 rq-grid-bg opacity-40" />
        <div className="absolute inset-0 opacity-60"><CyberBackground /></div>
        <div className="rq-container relative z-10 max-w-3xl">
          <Link to="/case-studies" className="inline-flex items-center gap-2 text-sm text-[color:var(--rq-green)] mb-6" data-testid="case-back-link">
            <ArrowLeft size={16} style={{ transform: flip }} />
            {t.res.backCase}
          </Link>
          <span className="inline-flex items-center gap-2 rounded-full border border-[rgba(46,230,110,0.3)] bg-[rgba(46,230,110,0.08)] px-3 py-1 text-xs font-semibold text-[color:var(--rq-green)] mb-4">
            <Building2 size={13} /> {t.res.clientLabel}: {cs.client}
          </span>
          <h1 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight">{cs.title}</h1>
        </div>
      </section>

      <section className="pb-8">
        <div className="rq-container max-w-5xl">
          <div className="rq-card overflow-hidden">
            <img src={cs.image} alt={cs.title} className="w-full h-[280px] md:h-[420px] object-cover" />
          </div>
        </div>
      </section>

      <section className="py-12 rq-light">
        <div className="rq-container grid lg:grid-cols-3 gap-10 items-start">
          <div className="lg:col-span-2">
            <ArticleBlocks blocks={cs.blocks} />
          </div>
          <div className="lg:col-span-1">
            <div className="lg:sticky lg:top-24">
              <ConsultForm source={`Case Study: ${cs.title}`} />
            </div>
          </div>
        </div>
      </section>

      <section className="py-14">
        <div className="rq-container">
          <h2 className="font-display text-2xl font-bold text-white mb-8">{t.res.relatedCases}</h2>
          <div className="grid gap-6 md:grid-cols-2">
            {others.map((c) => (
              <Link key={c.slug} to={`/case-studies/${c.slug}`} className="rq-card overflow-hidden flex group" data-testid={`related-case-${c.slug}`}>
                <img src={c.image} alt={c.title} className="w-32 md:w-44 object-cover shrink-0" />
                <div className="p-5">
                  <span className="text-xs font-semibold text-[color:var(--rq-green)]">{c.client}</span>
                  <h3 className="font-display mt-2 text-base font-semibold text-white leading-snug">{c.title}</h3>
                  <span className="mt-3 inline-flex items-center gap-1.5 text-sm text-[color:var(--rq-green)]">
                    {t.res.readMore}
                    <ArrowRight size={14} style={{ transform: flip }} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
      <CTABand />
    </div>
  );
}
